// First, define the structure of our translations
interface TranslationStrings {
  // General
  'app.name': string;
  'nav.home': string;
  'nav.quiz': string;
  'nav.challenges': string;
  'nav.interviews': string;
  'nav.references': string;
  'nav.startPractice': string;
  'nav.tour': string;
  'nav.feed': string;

  // Interview types
  'interview.technical': string;
  'interview.behavioral': string;
  'interview.system': string;
  'interview.pair': string;
  
  // Experience levels
  'level.junior': string;
  'level.mid': string;
  'level.senior': string;

  // AI prompts
  'ai.interviewer.role': string;
  'ai.assistant.help': string;

  // Quiz translations
  'quiz.title': string;
  'quiz.beforeStart': string;
  'quiz.totalQuestions': string;
  'quiz.difficultyLevels': string;
  'quiz.multipleAnswers': string;
  'quiz.navigation': string;
  'quiz.timing': string;
  'quiz.submit': string;
  'quiz.question': string;
  'quiz.of': string;

  // Quiz Results
  'quiz.completed': string;
  'quiz.resultsSummary': string;
  'quiz.accuracy': string;
  'quiz.score': string;
  'quiz.timeTaken': string;
  'quiz.detailedResults': string;
  'quiz.yourAnswer': string;
  'quiz.correctAnswer': string;
  'quiz.tryAgain': string;

  // Navigation
  'navigation.next': string;
  'navigation.previous': string;
  'navigation.submit': string;
  'navigation.finish': string;

  // Quiz
  'quiz.attempted': string;
  'quiz.skipped': string;
  'quiz.totalTime': string;

  // Quiz start
  'quiz.start': string;

  // About Page
  'about.title': string;
  'about.description': string;
  'about.whatIsLyfy': string;
  'about.team': string;
  'about.contactUs': string;

  // Footer
  'footer.description': string;
  'footer.quickLinks': string;
  'footer.resources': string;
  'footer.contact': string;
  'footer.rights': string;
}

// Define the available languages
export type Language = 'en' | 'fr' | 'ar';

// Define the structure of all translations
export type Translations = Record<Language, TranslationStrings>;

// Define the translation key type
export type TranslationKey = keyof TranslationStrings;

// Export the translations object
export const translations: Translations = {
  en: {
    'app.name': '.NET Mastery',
    'nav.home': 'Home',
    'nav.quiz': 'Quiz',
    'nav.challenges': 'Challenges',
    'nav.interviews': 'Interviews',
    'nav.references': 'References',
    'nav.startPractice': 'Start Practice',
    'nav.tour': 'Tour',
    'nav.feed': 'Feed',

    'interview.technical': 'Technical Interview',
    'interview.behavioral': 'Behavioral Interview',
    'interview.system': 'System Design',
    'interview.pair': 'Pair Programming',

    'level.junior': 'Junior Level',
    'level.mid': 'Mid Level',
    'level.senior': 'Senior Level',

    'ai.interviewer.role': 'You are conducting a {level} level {type} interview for a {company} company focusing on .NET/C# development.',
    'ai.assistant.help': 'I am your AI coding assistant. I can help you with your code and provide feedback.',

    'quiz.title': '.NET Knowledge Quiz',
    'quiz.beforeStart': 'Before You Start',
    'quiz.totalQuestions': '{count} questions in total',
    'quiz.difficultyLevels': 'Questions vary in difficulty level',
    'quiz.multipleAnswers': 'Some questions may have multiple correct answers',
    'quiz.navigation': 'You can navigate between questions',
    'quiz.timing': 'Your time will be tracked',
    'quiz.submit': 'Submit Quiz',
    'quiz.question': 'Question',
    'quiz.of': 'of',

    'quiz.completed': 'Quiz Completed!',
    'quiz.resultsSummary': "Here's how you performed",
    'quiz.accuracy': 'Accuracy',
    'quiz.score': 'Score',
    'quiz.timeTaken': 'Time Taken',
    'quiz.detailedResults': 'Detailed Results',
    'quiz.yourAnswer': 'Your Answer',
    'quiz.correctAnswer': 'Correct Answer',
    'quiz.tryAgain': 'Try Again',

    'navigation.next': 'Next',
    'navigation.previous': 'Previous',
    'navigation.submit': 'Submit',
    'navigation.finish': 'Finish',

    'quiz.attempted': 'Questions Attempted',
    'quiz.skipped': 'Questions Skipped',
    'quiz.totalTime': 'Total Time',

    'quiz.start': 'Start Quiz',

    'about.title': 'About LYFY',
    'about.description': 'Meet the team behind .NET Mastery - a project by LYFY LTD',
    'about.whatIsLyfy': 'What is LYFY?',
    'about.team': 'Our Team',
    'about.contactUs': 'Contact Us',

    'footer.description': 'Empowering developers with interactive learning tools for .NET mastery.',
    'footer.quickLinks': 'Quick Links',
    'footer.resources': 'Resources',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
  },
  fr: {
    'app.name': 'Maîtrise .NET',
    'nav.home': 'Accueil',
    'nav.quiz': 'Quiz',
    'nav.challenges': 'Défis',
    'nav.interviews': 'Entretiens',
    'nav.references': 'Références',
    'nav.startPractice': 'Commencer',
    'nav.tour': 'Visite',
    'nav.feed': 'Actualités',

    'interview.technical': 'Entretien Technique',
    'interview.behavioral': 'Entretien Comportemental',
    'interview.system': 'Conception Système',
    'interview.pair': 'Programmation en Binôme',

    'level.junior': 'Niveau Junior',
    'level.mid': 'Niveau Intermédiaire',
    'level.senior': 'Niveau Senior',

    'ai.interviewer.role': 'Vous menez un entretien {type} de niveau {level} pour une entreprise {company} axée sur le développement .NET/C#.',
    'ai.assistant.help': 'Je suis votre assistant de codage IA. Je peux vous aider avec votre code et fournir des commentaires.',

    'quiz.title': '.NET Knowledge Quiz',
    'quiz.beforeStart': 'Before You Start',
    'quiz.totalQuestions': '{count} questions in total',
    'quiz.difficultyLevels': 'Questions vary in difficulty level',
    'quiz.multipleAnswers': 'Some questions may have multiple correct answers',
    'quiz.navigation': 'You can navigate between questions',
    'quiz.timing': 'Your time will be tracked',
    'quiz.submit': 'Submit Quiz',
    'quiz.question': 'Question',
    'quiz.of': 'of',

    'quiz.completed': 'Quiz Terminé !',
    'quiz.resultsSummary': 'Voici vos résultats',
    'quiz.accuracy': 'Précision',
    'quiz.score': 'Score',
    'quiz.timeTaken': 'Temps Écoulé',
    'quiz.detailedResults': 'Résultats Détaillés',
    'quiz.yourAnswer': 'Votre Réponse',
    'quiz.correctAnswer': 'Bonne Réponse',
    'quiz.tryAgain': 'Réessayer',

    'navigation.next': 'Suivant',
    'navigation.previous': 'Précédent',
    'navigation.submit': 'Soumettre',
    'navigation.finish': 'Terminer',

    'quiz.attempted': 'Questions Tentées',
    'quiz.skipped': 'Questions Passées',
    'quiz.totalTime': 'Temps Total',

    'quiz.start': 'Commencer le Quiz',

    'about.title': 'À propos de LYFY',
    'about.description': 'Rencontrez l\'équipe derrière .NET Mastery - un projet de LYFY LTD',
    'about.whatIsLyfy': 'Qu\'est-ce que LYFY ?',
    'about.team': 'Notre Équipe',
    'about.contactUs': 'Contactez-nous',

    'footer.description': 'Donnons aux développeurs les outils d\'apprentissage interactifs pour maîtriser .NET.',
    'footer.quickLinks': 'Liens Rapides',
    'footer.resources': 'Ressources',
    'footer.contact': 'Contact',
    'footer.rights': 'Tous droits réservés.',
  },
  ar: {
    'app.name': 'إتقان .NET',
    'nav.home': 'الرئيسية',
    'nav.quiz': 'اختبار',
    'nav.challenges': 'تحديات',
    'nav.interviews': 'مقابلات',
    'nav.references': 'مراجع',
    'nav.startPractice': 'ابدأ التدريب',
    'nav.tour': 'جولة',
    'nav.feed': 'المنشورات',

    'interview.technical': 'مقابلة تقنية',
    'interview.behavioral': 'مقابلة سلوكية',
    'interview.system': 'تصميم النظام',
    'interview.pair': 'البرمجة الثنائية',

    'level.junior': 'مستوى مبتدئ',
    'level.mid': 'مستوى متوسط',
    'level.senior': 'مستوى متقدم',

    'ai.interviewer.role': 'أنت تجري مقابلة {type} لمستوى {level} لشركة {company} تركز على تطوير .NET/C#.',
    'ai.assistant.help': 'أنا مساعد البرمجة الخاص بك. يمكنني مساعدتك في الكود وتقديم الملاحظات.',

    'quiz.title': '.NET Knowledge Quiz',
    'quiz.beforeStart': 'Before You Start',
    'quiz.totalQuestions': '{count} questions in total',
    'quiz.difficultyLevels': 'Questions vary in difficulty level',
    'quiz.multipleAnswers': 'Some questions may have multiple correct answers',
    'quiz.navigation': 'You can navigate between questions',
    'quiz.timing': 'Your time will be tracked',
    'quiz.submit': 'Submit Quiz',
    'quiz.question': 'Question',
    'quiz.of': 'of',

    'quiz.completed': '!اكتمل الاختبار',
    'quiz.resultsSummary': 'إليك كيف كان أداؤك',
    'quiz.accuracy': 'الدقة',
    'quiz.score': 'النتجة',
    'quiz.timeTaken': 'الوقت المستغرق',
    'quiz.detailedResults': 'النتائج التفصيلية',
    'quiz.yourAnswer': 'إجابتك',
    'quiz.correctAnswer': 'الإجابة الصحيحة',
    'quiz.tryAgain': 'حاول مرة أخرى',

    'navigation.next': 'التالي',
    'navigation.previous': 'السابق',
    'navigation.submit': 'إرسال',
    'navigation.finish': 'إنهاء',

    'quiz.attempted': 'الأسئلة المحاولة',
    'quiz.skipped': 'الأسئلة التخطي',
    'quiz.totalTime': 'الوقت الكلي',

    'quiz.start': 'ابدأ الاختبار',

    'about.title': 'عن LYFY',
    'about.description': 'تعرف على الفريق وراء .NET Mastery - مشروع من LYFY LTD',
    'about.whatIsLyfy': 'ما هو LYFY؟',
    'about.team': 'فريقنا',
    'about.contactUs': 'اتصل بنا',

    'footer.description': 'تمكين المطورين بأدوات تعليمية تفاعلية لإتقان .NET',
    'footer.quickLinks': 'روابط سريعة',
    'footer.resources': 'المصادر',
    'footer.contact': 'اتصل بنا',
    'footer.rights': 'جميع الحقوق محفوظة.',
  }
}; 